// Leave-Shift Conflict Resolution Hooks
// Phase 4 of LEAVE_SHIFT_CONFLICT_RESOLUTION_IMPLEMENTATION.md

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaveService } from '@/services/leave/leaveService';
import { leaveQueryKeys } from './queryKeys';
import { scheduleKeys } from '@/hooks/schedule/queryKeys';
import { accrualHistoryQueryKeys } from './useAccrualHistory';
import { useToast } from '@/hooks/use-toast';
import type { ConflictResolutionPayload, ConflictResolutionResult } from '@/services/leave/conflictTypes';

export const useCheckLeaveConflicts = (
  profileId: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
  startTime?: string | null,
  endTime?: string | null,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: leaveQueryKeys.conflicts(profileId || '', startDate || '', endDate || ''),
    queryFn: () => leaveService.checkLeaveConflicts(
      profileId!, startDate!, endDate!, startTime, endTime
    ),
    enabled: enabled && !!profileId && !!startDate && !!endDate,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useEligibleReplacements = (
  shiftIds: string[],
  companyId: string | undefined,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: leaveQueryKeys.eligibleReplacements(shiftIds),
    queryFn: () => leaveService.getEligibleReplacements(shiftIds, companyId!),
    enabled: enabled && shiftIds.length > 0 && !!companyId,
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useApproveLeaveWithResolution = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: (payload: ConflictResolutionPayload) => 
      leaveService.approveLeaveWithResolution(payload),
    onSuccess: (data: ConflictResolutionResult) => {
      // Invalidate leave queries
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.requests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      queryClient.invalidateQueries({ queryKey: accrualHistoryQueryKeys.all });
      
      // Invalidate leave overlay queries (for schedule views)
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.leaveOverlay() });
      
      // Invalidate schedule queries (CRITICAL for F6 mitigation)
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      
      toast({
        title: "Leave Approved",
        description: `Leave approved. ${data.shifts_removed || 0} shifts removed, ${data.shifts_reassigned || 0} shifts reassigned.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Approval Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
